import React from "react";
import { Target, TrendingUp, Check, GraduationCap } from "lucide-react";
import { Reveal, Overline } from "../components/site/Reveal";
import { CASE_STUDIES, CAPABILITIES } from "../data/content";

export const CaseStudies = () => (
  <section id="case-studies" className="relative bg-bg-light py-24 sm:py-32" data-testid="case-studies-section">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <div className="max-w-2xl">
        <Reveal><Overline>Selected Case Studies</Overline></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-6 font-serif text-4xl font-light leading-tight text-charcoal sm:text-5xl">
            Real challenges, <span className="text-gradient-emerald">measurable outcomes</span>.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-base leading-relaxed text-charcoal/70">
            A selection of Anthony's work across healthcare manufacturing, the public sector, advanced materials and FMCG —
            each grounded in operational strategy, planning discipline and continuous improvement.
          </p>
        </Reveal>
      </div>

      <div className="mt-16 grid gap-6 lg:grid-cols-2">
        {CASE_STUDIES.map((cs, i) => (
          <Reveal key={cs.company} delay={(i % 2) * 0.1}>
            <article data-testid={`case-study-${i}`}
              className="group relative flex h-full flex-col overflow-hidden rounded-[1.75rem] border border-black/5 bg-white p-8 card-shadow transition-transform duration-500 hover:-translate-y-1.5 sm:p-10">
              <div className="absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-gradient-to-r from-emerald-base via-emerald-light to-gold transition-transform duration-500 group-hover:scale-x-100" />
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold">{cs.sector}</span>
                  <h3 className="mt-2 font-serif text-3xl text-charcoal">{cs.company}</h3>
                  <p className="mt-1 text-sm font-semibold text-emerald-base">{cs.role}</p>
                  <p className="mt-0.5 text-xs text-charcoal/50">{cs.period}</p>
                </div>
                <span className="font-serif text-5xl leading-none text-charcoal/10">0{i + 1}</span>
              </div>

              <div className="mt-7 rounded-2xl bg-bg-light p-5">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-charcoal/50">
                  <Target size={15} className="text-emerald-base" /> The Challenge
                </div>
                <p className="mt-2.5 text-sm leading-relaxed text-charcoal/75">{cs.challenge}</p>
              </div>

              <div className="mt-6 flex-1">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-charcoal/50">
                  <TrendingUp size={15} className="text-gold" /> Outcome & Impact
                </div>
                <ul className="mt-3.5 space-y-2.5">
                  {cs.impacts.map((it) => (
                    <li key={it} className="flex items-start gap-2.5 text-sm leading-relaxed text-charcoal/75">
                      <Check size={16} className="mt-0.5 shrink-0 text-emerald-light" /> {it}
                    </li>
                  ))}
                </ul>
              </div>

              {cs.training && (
                <div className="mt-6 flex items-start gap-2.5 rounded-2xl border border-gold/25 bg-gold/5 p-4">
                  <GraduationCap size={17} className="mt-0.5 shrink-0 text-gold" />
                  <p className="text-sm leading-relaxed text-charcoal/75">
                    <span className="font-semibold text-emerald-base">Training delivered — </span>{cs.training}
                  </p>
                </div>
              )}
            </article>
          </Reveal>
        ))}
      </div>

      <div className="mt-20">
        <Reveal><Overline>Capability & Training Delivered</Overline></Reveal>
        <Reveal delay={0.05}>
          <h3 className="mt-6 max-w-3xl font-serif text-3xl font-light leading-tight text-charcoal sm:text-4xl">
            Alongside delivery, building <span className="text-gradient-gold">lasting team capability</span>.
          </h3>
        </Reveal>
        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          {CAPABILITIES.map((c, i) => (
            <Reveal key={c.area} delay={(i % 2) * 0.08}>
              <div data-testid={`capability-${i}`}
                className="group h-full rounded-2xl border border-black/5 bg-white p-6 transition-[transform,border-color] duration-500 hover:-translate-y-1 hover:border-gold/40">
                <h4 className="font-serif text-xl text-emerald-base">{c.area}</h4>
                <p className="mt-2.5 text-sm leading-relaxed text-charcoal/70">{c.detail}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);
